/**
 * Piège le focus clavier dans un conteneur (WCAG 2.1.2 / 2.4.3).
 * Retourne une fonction de nettoyage qui restaure le focus précédent.
 */
export function trapFocus(
  container: HTMLElement,
  opts?: {
    /** Si fourni : Échap appelle ce callback (modales fermables). Sinon : Échap reste dans le dialogue (CGU). */
    onEscape?: (event: KeyboardEvent) => void;
    /** Restaurer le focus à l’élément actif avant ouverture (défaut true). */
    restoreFocus?: boolean;
  },
): () => void {
  const selector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  const collect = (root: ParentNode): HTMLElement[] => {
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
    );
    // Contenu sloté (light DOM) + shadow
    if (root instanceof HTMLElement && root.shadowRoot) {
      nodes.push(...collect(root.shadowRoot));
    }
    return nodes;
  };

  const isInside = (el: Node | null): boolean => {
    if (!el) return false;
    if (container === el || container.contains(el)) return true;
    const root = el.getRootNode();
    if (root instanceof ShadowRoot) {
      if (root.host === container) return true;
      return isInside(root.host);
    }
    return false;
  };

  const focusables = (): HTMLElement[] =>
    collect(container).filter((el) => {
      try {
        return el.offsetParent !== null || el === container;
      } catch {
        return false;
      }
    });

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (opts?.onEscape) {
        opts.onEscape(event);
        return;
      }
      // Dialogues bloquants (CGU) : Échap ne ferme pas
      event.preventDefault();
      event.stopPropagation();
      const nodes = focusables();
      (nodes[0] ?? container).focus();
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = focusables();
    if (nodes.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }
    const first = nodes[0]!;
    const last = nodes[nodes.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (event.shiftKey) {
      if (!active || active === first || !isInside(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (!active || active === last || !isInside(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  container.addEventListener('keydown', onKeyDown);
  if (!container.hasAttribute('tabindex')) {
    container.setAttribute('tabindex', '-1');
  }
  const initial = focusables()[0] ?? container;
  requestAnimationFrame(() => initial.focus());

  return () => {
    container.removeEventListener('keydown', onKeyDown);
    if (opts?.restoreFocus !== false && previouslyFocused && document.contains(previouslyFocused)) {
      try {
        previouslyFocused.focus();
      } catch {
        /* détaché */
      }
    }
  };
}
